import { useCallback, useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import useCaptureCamera from 'views/DigitalTrayMapping/CaptureTray/CaptureCamera/CaptureCamera.logic'
import { useAddTrayContext } from '../AddTray.context'
import { useInventorySheetsContext } from '../InventorySheets/InventorySheets.context'
import { InventorySheetFile } from '../InventorySheets/InventorySheets.types'
import { useTrayStepContext } from '../AddTray.logic'

const useParentTrayImageLogic = () => {
  const {
    handleSPDCaptureImage,
    image,
    videoRef,
    cameras,
    activeCamera,
    fetchCameraData,
    isCameraMenuOpen,
    handleSelectCamera,
    handleToggleCameraMenu,
    orientation,
    videoHeight,
  } = useCaptureCamera()

  const { uploadFile } = useInventorySheetsContext()
  const { parentTrayImage, setParentTrayImage } = useAddTrayContext()
  const { nextStep, prevStep } = useTrayStepContext()

  const [isUploading, setIsUploading] = useState<boolean>(false)

  const isUploadingRef = useRef(false)
  const currentImageRef = useRef<string | null>(null)
  const mounted = useRef(true)

  useEffect(() => {
    if (image) {
      const saveTrayImage = async () => {
        const imgId: string = uuidv4()
        const newTrayImage: InventorySheetFile = {
          id: imgId,
          src: image,
          isUploading: true,
          isUploaded: false,
          mimeType: 'image/jpeg',
        }
        setParentTrayImage(newTrayImage)

        await uploadAndUpdateTrayImage(imgId)
      }

      saveTrayImage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image])

  const uploadAndUpdateTrayImage = useCallback(
    async (imgId: string) => {
      try {
        // If already uploading or this image was already processed, skip
        if (isUploadingRef.current || currentImageRef.current === image) {
          return
        }

        // Set multiple flags to prevent any other actions
        isUploadingRef.current = true
        setIsUploading(true)

        // Create a local variable to hold the result
        let uploadSuccess = false
        let trayImageURL = null

        try {
          // Use the already optimized image from useCaptureCamera
          trayImageURL = await uploadFile(imgId, image, 'image/jpeg', false)
          uploadSuccess = !!trayImageURL
        } catch (uploadError) {
          console.error('Upload failed:', uploadError)
          uploadSuccess = false
        }

        // Only proceed if component is still mounted
        if (!mounted.current) return

        if (uploadSuccess && trayImageURL) {
          // Create the new tray image object
          const newTrayImage = {
            id: imgId,
            src: image,
            isUploading: false,
            isUploaded: true,
            mimeType: 'image/jpeg',
          } as InventorySheetFile

          // Use requestAnimationFrame to ensure DOM is ready
          requestAnimationFrame(() => {
            // Delay state update slightly to prevent iOS refresh trigger
            setTimeout(() => {
              if (mounted.current) {
                // Update state in a single batched update
                setParentTrayImage(newTrayImage)
                setIsUploading(false)

                // Update refs last
                currentImageRef.current = image
                isUploadingRef.current = false
              }
            }, 150) // Slightly longer delay
          })
        } else {
          // Handle failure
          if (mounted.current) {
            setParentTrayImage(undefined)
            setIsUploading(false)
            isUploadingRef.current = false
          }
        }
      } catch (error) {
        // Final error boundary
        console.error('Error in entire upload process:', error)

        if (mounted.current) {
          setParentTrayImage(undefined)
          setIsUploading(false)
          isUploadingRef.current = false
        }
      }
    },
    [uploadFile, image, setParentTrayImage, setIsUploading, mounted]
  )

  const handleImageCapture = (skipRotate?: boolean) => {
    handleSPDCaptureImage(skipRotate)
  }

  const handleBackClick = () => {
    prevStep()
  }

  const handleConfirm = () => {
    if (parentTrayImage && !parentTrayImage.isUploading) {
      nextStep()
    }
  }

  const recapture = () => {
    setParentTrayImage(undefined)
    fetchCameraData()
  }

  return {
    handleImageCapture,
    videoRef,
    cameras,
    activeCamera,
    isCameraMenuOpen,
    handleSelectCamera,
    handleToggleCameraMenu,
    handleBackClick,
    handleConfirm,
    parentTrayImage,
    recapture,
    orientation,
    videoHeight,
    isUploading,
  }
}

export default useParentTrayImageLogic
